import { DataObject } from '@fluidframework/aqueduct';
import { ActivityEventType } from '@metaos/mfs-telemetry';
import { assertPrecondition } from '../errors';
import { logActivity, logVerbose } from '../log';
import { MfsHost } from '../store/mfsHost';
import {
  MfsInternalSessionData,
  MfsDefinedValue,
  MfsSessionData,
  MfsSessionDataChange,
  MfsVolatileSession,
  MfsVolatileSessionEvents,
  SessionDataId,
} from '../volatileSession';
import {
  isMfsSessionData,
  getMfsInternalSessionData,
  isMfsDeleteSignalData,
  isMfsGetSessionDataResponse,
} from '../volatileSession/mfsVolatileUtils';
import { MfsSignalManager, MfsSignalType, SessionDataMapType } from './signals';

/** A Fluid {@link DataObject} implementation of {@link MfsVolatileSession} interface. */
export class MfsFluidVolatileSession extends DataObject implements MfsVolatileSession {
  #app!: MfsHost;
  #id!: string;
  sessionDataMap!: SessionDataMapType;
  signalManager!: MfsSignalManager;

  /** @private */
  registerVolatileAudience(onlyVolatileSession: boolean): void {
    if (!onlyVolatileSession) {
      return;
    }
    const audience = this.runtime.getAudience();
    audience.on('addMember', (clientId: string) => {
      this.emitMfsVolatileSessionEvent('audienceChanged', { clientId: clientId, status: 'add' });
    });
    audience.on('removeMember', (clientId: string) => {
      this.emitMfsVolatileSessionEvent('audienceChanged', { clientId: clientId, status: 'remove' });
    });
  }

  initSession(): void {
    this.sessionDataMap = new Map();
    this.signalManager = new MfsSignalManager(this.#app.id, this.runtime, this.context);
    this.signalManager.on('signalReceived', this.handleDataFromSignal.bind(this));
  }

  /** Set host */
  setHost(app: MfsHost): void {
    this.#app = app;
  }

  /** @inheritdoc */
  getHost(): MfsHost {
    return this.#app;
  }

  /** Set pod ID */
  setId(id: string): void {
    this.#id = id;
  }

  /** @inheritdoc */
  getId(): string {
    return this.#id;
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  setData(sessionData: MfsSessionData): void {
    logVerbose('setData', `Setting session data`);
    // set data in map using last op sequence number
    const internalSessionData = getMfsInternalSessionData(sessionData, this.runtime.deltaManager.lastSequenceNumber);
    this.setDataInMap(internalSessionData, false);
    // submit signal
    this.signalManager.sendSetData(internalSessionData);
  }

  /** @inheritdoc */
  getData(dataSetId: string, sessionDataId: string): MfsSessionData | undefined {
    // get dataSet Map
    const existingDataSetMap = this.sessionDataMap.get(dataSetId);

    if (!existingDataSetMap) {
      return undefined;
    }

    // get sessionData
    const existingSessionData = existingDataSetMap.get(sessionDataId);

    if (!existingSessionData) {
      return undefined;
    }

    // need to return the session data object, without the seqNum
    const { seqNum, ...mfsSessionData } = existingSessionData;
    return mfsSessionData;
  }

  /** @inheritdoc */
  getDataSet(dataSetId: string): ReadonlyMap<string, MfsSessionData> | undefined {
    return this.getDataSetMapWithoutSeqNum(dataSetId);
  }

  /** @inheritdoc */
  getAllData(): ReadonlyMap<string, Map<string, MfsSessionData>> | undefined {
    const allDataMap = new Map<string, Map<string, MfsSessionData>>();
    for (const key of this.sessionDataMap.keys()) {
      const dataSetMap = this.getDataSetMapWithoutSeqNum(key);
      if (dataSetMap) {
        allDataMap.set(key, dataSetMap);
      }
    }
    return allDataMap;
  }
  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  deleteData(dataSetId: string, sessionDataId: string): void {
    const dataSetMap = this.sessionDataMap.get(dataSetId);
    if (!dataSetMap) {
      return;
    }

    if (dataSetMap.has(sessionDataId)) {
      dataSetMap.delete(sessionDataId);
      this.signalManager.sendDeleteData({ dataSetId, sessionDataId });
    }
  }
  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  deleteDataSet(dataSetId: string): void {
    if (this.sessionDataMap.has(dataSetId)) {
      this.sessionDataMap.delete(dataSetId);
      this.signalManager.sendDeleteData({ dataSetId });
    }
  }
  /** @inheritdoc */
  getParticipants(): string[] {
    return Array.from(this.runtime.getAudience().getMembers().keys());
  }

  /** Handle incoming signals */
  private handleDataFromSignal(
    signalType: MfsSignalType,
    data: MfsDefinedValue,
    referenceSequenceNumber: number
  ): void {
    logVerbose('handleDataFromSignal', `Received signal; signalType:${signalType}, seqNum: ${referenceSequenceNumber}`);
    if (signalType === 'setDataSignal') {
      this.processSetDataSignal(data, referenceSequenceNumber);
    }
    if (signalType === 'deleteDataSignal') {
      this.processDeleteDataSignal(data, referenceSequenceNumber);
    }
    if (signalType === 'getSessionDataRequest') {
      this.processGetSessionDataRequest();
    }
    if (signalType === 'getSessionDataResponse') {
      this.processGetSessionDataResponse(data);
    }
  }

  /** Process setData signal by updating sessionDataMap */
  private processSetDataSignal(data: MfsDefinedValue, seqNum: number): void {
    // check if data has all the fields of MfsSessionData type
    assertPrecondition(isMfsSessionData(data));
    const internalSessionData = getMfsInternalSessionData(data, seqNum);
    this.setDataInMap(internalSessionData, true);
  }

  /** Process deleteData signal by updating sessionDataMap */
  private processDeleteDataSignal(data: MfsDefinedValue, seqNum: number): void {
    assertPrecondition(isMfsDeleteSignalData(data));
    // delete the specific session data ID
    if (data.sessionDataId) {
      this.processDeleteSessionData(data.dataSetId, data.sessionDataId, seqNum);
    } else {
      // delete all the session data in the dataset, individually
      const existingDataSetMap = this.sessionDataMap.get(data.dataSetId);
      const sessionDataIds = Array.from(existingDataSetMap ? existingDataSetMap.keys() : new Map().keys());
      sessionDataIds.map((sessionDataId) => {
        this.processDeleteSessionData(data.dataSetId, sessionDataId, seqNum);
      });
    }
  }

  /** Process getSessionDataRequest signal by sending session data map */
  private processGetSessionDataRequest(): void {
    this.signalManager.sendSessionDataResponse(this.sessionDataMap);
  }

  /** Process getSessionDataResponse signal by updating in-memory map */
  private processGetSessionDataResponse(data: MfsDefinedValue): void {
    assertPrecondition(isMfsGetSessionDataResponse(data));
    data.internalSessionDataArray.map((sessionData) => this.setDataInMap(sessionData, true));
  }

  /** Deletes an individual session data object from the map */
  private processDeleteSessionData(dataSetId: string, sessionDataId: string, seqNum: number): void {
    const existingDataSetMap = this.sessionDataMap.get(dataSetId);
    const existingSessionData = existingDataSetMap?.get(sessionDataId);
    // if data set + session data exists and it's seqNum is lower than the incoming seqNum, need to delete
    if (existingDataSetMap && existingSessionData && existingSessionData.seqNum <= seqNum) {
      existingDataSetMap.delete(sessionDataId);
      // emit event
      this.processVolatileSessionEvent(dataSetId, sessionDataId, 'removed');
    }
  }

  /** Sets an MfsInternalSessionData object in sessionDataMap */
  private setDataInMap(internalSessionData: MfsInternalSessionData, processingIncomingSignal: boolean): void {
    // check if data set already exists in map
    const dataSetId = internalSessionData.dataSetId;
    const sessionDataId = internalSessionData.sessionDataId;
    const existingDataSetMap = this.sessionDataMap.get(dataSetId);

    // create new data set
    if (!existingDataSetMap) {
      const newSessionDataMap = new Map<SessionDataId, MfsInternalSessionData>();
      newSessionDataMap.set(sessionDataId, internalSessionData);
      this.sessionDataMap.set(dataSetId, newSessionDataMap);
      // only emit an event if the data change was made by an incoming signal
      if (processingIncomingSignal) {
        this.processVolatileSessionEvent(dataSetId, sessionDataId, 'set');
      }
      return;
    }

    // check if session data already is in map
    const existingSessionData = existingDataSetMap.get(sessionDataId);

    // if session data does not exist or has its seqNum is <= new sequence number, need to update
    if (!existingSessionData || existingSessionData.seqNum <= internalSessionData.seqNum) {
      existingDataSetMap.set(sessionDataId, internalSessionData);
      // only emit an event if the data change was made by an incoming signal
      if (processingIncomingSignal) {
        this.processVolatileSessionEvent(dataSetId, sessionDataId, 'set');
      }
    }
  }

  /** Create data change event to be emitted by volatile session */
  private processVolatileSessionEvent(dataSetId: string, sessionDataId: string, changeKind: 'set' | 'removed') {
    const dataEvent: MfsSessionDataChange = {
      dataSetId: dataSetId,
      sessionDataId: sessionDataId,
      changeKind: changeKind,
    };

    this.emitMfsVolatileSessionEvent('dataChanged', dataEvent);
  }

  /** Get dataSet map, with MfsSessionData instead of MfsInternalSessionData as the value */
  private getDataSetMapWithoutSeqNum(dataSetId: string): Map<string, MfsSessionData> | undefined {
    const existingDataSetMap = this.sessionDataMap.get(dataSetId);

    if (!existingDataSetMap) {
      return undefined;
    }

    /** Create a new map, whose values are of type MfsSessionData */
    const newDataSetMap = new Map<string, MfsSessionData>();
    for (const key of existingDataSetMap.keys()) {
      const existingSessionData = existingDataSetMap.get(key);

      if (existingSessionData) {
        /** Destructure internal session data to get session data object */
        const { seqNum, ...sessionData } = existingSessionData;
        newDataSetMap.set(key, sessionData);
      }
    }
    return newDataSetMap;
  }

  /** @protected */
  protected emitMfsVolatileSessionEvent<E extends keyof MfsVolatileSessionEvents>(
    event: E,
    arg: MfsVolatileSessionEvents[E]
  ): void {
    logVerbose('emitMfsVolatileSessionEvent', 'Emitting volatileSession event');
    this.emit(event, arg);
  }
}
